import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { corporateCostingProfileService } from 'corporate/services';
import { IAttachVendorQuotesPayload, IImportVendorQuotePayload } from 'corporate/types';

interface ICorporateCostingProfileActions {
  selectionCount?: number;
  position?: NotificationPosition;
}

export const useCorporateCostingProfileActions = ({
  selectionCount = 0,
  position,
}: ICorporateCostingProfileActions = {}) => {
  return {
    ...useDetachVendorQuotes(selectionCount, position),
    ...useAttachVendorQuotesToCostingProfile(selectionCount, position),
    ...useImportVendorQuoteToCostingProfile(position),
  };
};

const useDetachVendorQuotes = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageVendorQuotesDetachSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageVendorQuotesDetachInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleVendorQuotesDetach', { count: selectionCount }),
    },
    position,
  });

  const { mutateAsync } = useMutation(
    (data) => corporateCostingProfileService.detachVendorQuotes(data),
    {
      ...errors,
    }
  );

  return { detachVendorQuotes: mutateAsync };
};

const useAttachVendorQuotesToCostingProfile = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.DRAWER
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageVendorQuotesAttachSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageVendorQuotesAttachInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleVendorQuotesAttach', { count: selectionCount }),
    },
    position,
  });

  const { mutateAsync } = useMutation(
    (data: IAttachVendorQuotesPayload) => corporateCostingProfileService.attachVendorQuotes(data),
    {
      ...errors,
    }
  );

  return { attachVendorQuotesToCostingProfile: mutateAsync };
};

const useImportVendorQuoteToCostingProfile = (
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageVendorQuoteImportSuccess'),
    },
    loading: {
      message: t('messageVendorQuoteImportInProgress'),
    },
    error: {
      title: t('errorTitleVendorQuoteImport'),
    },
    position,
  });

  const { mutateAsync } = useMutation(
    (data: IImportVendorQuotePayload) => corporateCostingProfileService.importVendorQuote(data),
    {
      ...errors,
    }
  );

  return { importVendorQuoteToCostingProfile: mutateAsync };
};
